// Empire Apps Chassis · elder · onboarding
// 60-second elder onboarding state machine.
// Steps: language -> biometric -> family contacts -> emergency contacts -> medical info.
// Pure-state design: each transition returns the next state; UI is a thin renderer over this.

import type { LangCode } from "./types.ts";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "./types.ts";

export type OnboardingStep =
  | "language"
  | "biometric"
  | "family-contacts"
  | "emergency-contacts"
  | "medical-info"
  | "complete";

export const ONBOARDING_STEP_ORDER: readonly OnboardingStep[] = [
  "language",
  "biometric",
  "family-contacts",
  "emergency-contacts",
  "medical-info",
  "complete",
] as const;

export interface FamilyContact {
  readonly relation: "son" | "daughter" | "spouse" | "sibling" | "friend" | "other";
  readonly name: string;
  readonly phone: string;
  readonly canMirror: boolean;
  readonly canPin: boolean;
  readonly canAlert: boolean;
}

export interface EmergencyContact {
  readonly label: string;
  readonly phone: string;
  readonly priority: 1 | 2 | 3;
}

export interface MedicalInfo {
  readonly bloodGroup: string | null;
  readonly chronicConditions: readonly string[];
  readonly allergies: readonly string[];
  readonly currentMeds: readonly string[];
  readonly preferredPhysicianPhone: string | null;
  readonly abhaLinked: boolean;
}

export interface OnboardingState {
  readonly step: OnboardingStep;
  readonly lang: LangCode;
  readonly biometricEnrolled: boolean;
  readonly biometricKind: "webauthn" | "device-pin" | "none";
  readonly familyContacts: readonly FamilyContact[];
  readonly emergencyContacts: readonly EmergencyContact[];
  readonly medical: MedicalInfo;
  readonly completedAt: number | null;
  readonly version: string;
}

const STATE_KEY = "empire.elder.onboarding";
export const ONBOARDING_VERSION = "v1.2026-05" as const;

// Default emergency contacts seeded by app (national numbers — India)
const DEFAULT_EMERGENCY: readonly EmergencyContact[] = [
  { label: "Police", phone: "112", priority: 1 },
  { label: "Ambulance", phone: "108", priority: 2 },
  { label: "Senior Citizen Helpline", phone: "14567", priority: 3 },
];

export function initialState(): OnboardingState {
  return {
    step: "language",
    lang: DEFAULT_LANG,
    biometricEnrolled: false,
    biometricKind: "none",
    familyContacts: [],
    emergencyContacts: [...DEFAULT_EMERGENCY],
    medical: {
      bloodGroup: null,
      chronicConditions: [],
      allergies: [],
      currentMeds: [],
      preferredPhysicianPhone: null,
      abhaLinked: false,
    },
    completedAt: null,
    version: ONBOARDING_VERSION,
  };
}

export function loadState(): OnboardingState {
  if (typeof localStorage === "undefined") return initialState();
  const raw = localStorage.getItem(STATE_KEY);
  if (!raw) return initialState();
  try {
    const parsed = JSON.parse(raw) as Partial<OnboardingState>;
    return mergeWithDefaults(parsed);
  } catch {
    return initialState();
  }
}

function mergeWithDefaults(p: Partial<OnboardingState>): OnboardingState {
  const base = initialState();
  return {
    step: p.step ?? base.step,
    lang: (p.lang && (SUPPORTED_LANGS as readonly string[]).includes(p.lang)) ? p.lang : base.lang,
    biometricEnrolled: p.biometricEnrolled ?? base.biometricEnrolled,
    biometricKind: p.biometricKind ?? base.biometricKind,
    familyContacts: p.familyContacts ?? base.familyContacts,
    emergencyContacts: p.emergencyContacts && p.emergencyContacts.length > 0 ? p.emergencyContacts : base.emergencyContacts,
    medical: { ...base.medical, ...(p.medical ?? {}) },
    completedAt: p.completedAt ?? base.completedAt,
    version: ONBOARDING_VERSION,
  };
}

export function saveState(state: OnboardingState): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(STATE_KEY, JSON.stringify(state));
}

export function clearState(): void {
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(STATE_KEY);
}

// --- Pure transitions

export function pickLanguage(state: OnboardingState, lang: LangCode): OnboardingState {
  return { ...state, lang, step: "biometric" };
}

export function enrollBiometric(
  state: OnboardingState,
  kind: OnboardingState["biometricKind"],
): OnboardingState {
  return {
    ...state,
    biometricEnrolled: kind !== "none",
    biometricKind: kind,
    step: "family-contacts",
  };
}

export function setFamilyContacts(state: OnboardingState, contacts: readonly FamilyContact[]): OnboardingState {
  // require at least one family contact to advance
  if (contacts.length === 0) return state;
  return { ...state, familyContacts: contacts.slice(0, 5), step: "emergency-contacts" };
}

export function setEmergencyContacts(state: OnboardingState, contacts: readonly EmergencyContact[]): OnboardingState {
  // ensure 112 is always present
  const has112 = contacts.some((c) => c.phone === "112");
  const merged = has112 ? contacts : [{ label: "Police", phone: "112", priority: 1 as const }, ...contacts];
  return { ...state, emergencyContacts: merged.slice(0, 6), step: "medical-info" };
}

export function setMedicalInfo(state: OnboardingState, medical: Partial<MedicalInfo>): OnboardingState {
  return {
    ...state,
    medical: { ...state.medical, ...medical },
    step: "complete",
    completedAt: Date.now(),
  };
}

export function backStep(state: OnboardingState): OnboardingState {
  const idx = ONBOARDING_STEP_ORDER.indexOf(state.step);
  if (idx <= 0) return state;
  const previous = ONBOARDING_STEP_ORDER[idx - 1];
  if (previous === undefined) return state;
  return { ...state, step: previous };
}

export function isComplete(state: OnboardingState): boolean {
  return state.step === "complete" && state.completedAt !== null;
}

export function stepProgressRatio(state: OnboardingState): number {
  const idx = ONBOARDING_STEP_ORDER.indexOf(state.step);
  if (idx < 0) return 0;
  // "complete" is the terminal label; progress is (idx) / (count - 1)
  return idx / (ONBOARDING_STEP_ORDER.length - 1);
}

// --- Validation helpers

const PHONE_REGEX = /^(\+?91)?[6-9]\d{9}$|^\d{3,5}$/; // Indian mobile or short code

export function validatePhone(p: string): boolean {
  return PHONE_REGEX.test(p.replace(/[\s-]/g, ""));
}

export function validateFamilyContact(c: FamilyContact): readonly string[] {
  const errors: string[] = [];
  if (!c.name || c.name.length < 2) errors.push("name.too-short");
  if (!validatePhone(c.phone)) errors.push("phone.invalid");
  return errors;
}

export function validateEmergencyContact(c: EmergencyContact): readonly string[] {
  const errors: string[] = [];
  if (!c.label) errors.push("label.empty");
  if (!validatePhone(c.phone)) errors.push("phone.invalid");
  return errors;
}

// --- Export readiness helpers for app routes

export interface OnboardingSummary {
  readonly completedAt: number | null;
  readonly lang: LangCode;
  readonly familyCount: number;
  readonly emergencyCount: number;
  readonly hasBiometric: boolean;
  readonly hasMedical: boolean;
}

export function summarize(state: OnboardingState): OnboardingSummary {
  const med = state.medical;
  const hasMedical =
    med.bloodGroup !== null ||
    med.chronicConditions.length > 0 ||
    med.currentMeds.length > 0;
  return {
    completedAt: state.completedAt,
    lang: state.lang,
    familyCount: state.familyContacts.length,
    emergencyCount: state.emergencyContacts.length,
    hasBiometric: state.biometricEnrolled,
    hasMedical,
  };
}
